// Header compact utilisateur pour la Fan Zone
import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Trophy, Star, TrendingUp, Settings } from 'lucide-react';
import { LEVELS_CONFIG } from '../services/fanZoneConfig';

// Couleurs de profil disponibles
const PROFILE_COLORS = {
  yellow: '#FFE400',
  lime: '#BDFF00',
  blue: '#3B82F6',
  purple: '#8B5CF6',
  red: '#EF4444',
  orange: '#F97316',
};

const UserHeader = ({ userProfile, rank, totalPlayers }) => {
  const navigate = useNavigate();
  
  if (!userProfile) return null;

  const { pseudo, totalXP, levelInfo, avatar, profileColor } = userProfile;
  const levelColor = levelInfo?.color || LEVELS_CONFIG[0].color;
  const userColor = PROFILE_COLORS[profileColor] || levelColor;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full"
    >
      <div 
        className="relative overflow-hidden rounded-2xl p-4 md:p-6"
        style={{
          background: 'linear-gradient(135deg, rgba(255,228,0,0.1) 0%, rgba(189,255,0,0.05) 100%)',
          border: '1px solid rgba(255,228,0,0.2)',
        }}
      >
        {/* Decorative glow */}
        <div 
          className="absolute -top-20 -right-20 w-40 h-40 rounded-full blur-3xl opacity-20"
          style={{ background: levelColor }}
        />
        
        <div className="relative flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Avatar & Pseudo */}
          <div 
            className="flex items-center gap-4 cursor-pointer group"
            onClick={() => navigate('/fanzone/profil')}
          >
            {/* Avatar avec badge de niveau */}
            <div className="relative">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center text-2xl transition-transform group-hover:scale-105"
                style={{
                  background: `linear-gradient(135deg, ${userColor}40, ${userColor}20)`,
                  border: `2px solid ${userColor}`,
                }}
              >
                {avatar || pseudo?.charAt(0)?.toUpperCase() || 'F'}
              </div>
              {/* Badge niveau */}
              <div 
                className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-black"
                style={{ background: userColor }}
              >
                {levelInfo?.level || 1}
              </div>
              {/* Icône edit au hover */}
              <div className="absolute inset-0 rounded-full flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity">
                <Settings className="w-5 h-5 text-white" />
              </div>
            </div>
            
            {/* Info utilisateur */}
            <div>
              <h2 className="text-xl font-bold text-white group-hover:text-[#FFE400] transition-colors">
                {pseudo || 'Fan EFAFC'}
              </h2>
              <p className="text-sm" style={{ color: userColor }}>
                {levelInfo?.title || 'Nouveau Fan'}
              </p>
            </div>
          </div>
          
          {/* Stats */}
          <div className="flex items-center gap-6">
            {/* XP */}
            <div className="text-center">
              <div className="flex items-center gap-1 text-[#FFE400]">
                <Star className="w-4 h-4" />
                <span className="text-lg font-bold">{totalXP?.toLocaleString() || 0}</span>
              </div>
              <p className="text-xs text-gray-400">XP</p>
            </div>
            
            {/* Rang */}
            {rank > 0 && (
              <div className="text-center">
                <div className="flex items-center gap-1 text-[#BDFF00]">
                  <Trophy className="w-4 h-4" />
                  <span className="text-lg font-bold">#{rank}</span>
                </div>
                <p className="text-xs text-gray-400">sur {totalPlayers}</p>
              </div>
            )}
            
            {/* Progression */}
            {levelInfo?.nextLevel && (
              <div className="hidden md:block text-center min-w-[100px]">
                <div className="flex items-center gap-1 text-gray-300">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-sm">{levelInfo.xpForNext} XP</span>
                </div>
                <div className="w-full h-1.5 bg-gray-700 rounded-full mt-1 overflow-hidden">
                  <motion.div 
                    initial={{ width: 0 }}
                    animate={{ width: `${levelInfo.progressToNext}%` }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    className="h-full rounded-full"
                    style={{ background: `linear-gradient(90deg, #FFE400, #BDFF00)` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-0.5">Prochain niveau</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default UserHeader;
